import { supabase } from "@/integrations/supabase/client";

// Security utilities for data filtering and validation

export interface ArtistProfileSafe {
  id: string;
  artist_type: string;
  city?: string;
  country?: string;
  created_at: string;
  // Exclude sensitive fields like portfolio_file_url, instagram_url, etc.
}

export interface ArtistProfileFull {
  id: string;
  artist_type: string;
  city?: string;
  country?: string;
  portfolio_file_url?: string;
  instagram_url?: string;
  youtube_url?: string;
  website_url?: string;
  open_to_yacht_bookings?: boolean;
  created_at: string;
  updated_at: string;
  auth_profile_id: string;
}

/**
 * Filters artist profile data to only include safe fields for public viewing
 */
export const filterArtistProfileForPublic = (profile: ArtistProfileFull): ArtistProfileSafe => {
  return {
    id: profile.id,
    artist_type: profile.artist_type,
    city: profile.city,
    country: profile.country,
    created_at: profile.created_at,
  };
};

/**
 * Validates email format with comprehensive security checks
 */
export const validateEmailFormat = (email: string): boolean => {
  if (!email || typeof email !== 'string') return false;
  
  // Basic format check
  const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
  if (!emailRegex.test(email)) return false;
  
  // Length check (RFC 5321 limit)
  if (email.length > 320) return false;
  
  // No consecutive dots
  if (email.includes('..')) return false;
  
  // No leading or trailing dots
  if (email.startsWith('.') || email.endsWith('.')) return false;
  
  // @ position checks
  const atIndex = email.indexOf('@');
  if (atIndex <= 0 || atIndex >= email.length - 1) return false;
  
  return true;
};

/**
 * Sanitizes input to prevent XSS attacks
 */
export const sanitizeInput = (input: string): string => {
  if (!input || typeof input !== 'string') return '';
  
  return input
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;')
    .trim();
};

/**
 * Validates and sanitizes form data
 */
export const validateFormData = (data: Record<string, any>): { isValid: boolean; errors: string[]; sanitized: Record<string, any> } => {
  const errors: string[] = [];
  const sanitized: Record<string, any> = {};
  
  for (const [key, value] of Object.entries(data)) {
    if (typeof value === 'string') {
      // Sanitize string inputs
      sanitized[key] = sanitizeInput(value);
      
      // Validate email fields
      if (key === 'email' && !validateEmailFormat(value)) {
        errors.push('Invalid email format');
      }
      
      // Check for excessively long inputs
      if (value.length > 1000) {
        errors.push(`${key} exceeds maximum length`);
      }
    } else {
      sanitized[key] = value;
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    sanitized
  };
};

/**
 * Logs security events with proper context
 */
export const logSecurityEvent = async (
  actionType: string,
  resourceType: string,
  resourceId?: string,
  metadata?: Record<string, any>
): Promise<void> => {
  try {
    // Log locally for debugging instead of trying to call the edge function
    console.log('Security Event:', {
      action_type: actionType,
      resource_type: resourceType,
      resource_id: resourceId,
      metadata: {
        timestamp: new Date().toISOString(),
        user_agent: typeof window !== 'undefined' ? navigator.userAgent : 'unknown',
        url: typeof window !== 'undefined' ? window.location.href : 'unknown',
        ...metadata
      }
    });
  } catch (error) {
    console.log('Security logging info:', actionType, resourceType, metadata);
  }
};

/**
 * Rate limiting check for client-side operations
 */
export const checkRateLimit = (key: string, maxRequests: number, windowMs: number): boolean => {
  const now = Date.now();
  const windowKey = `rateLimit_${key}`;
  
  try {
    const stored = localStorage.getItem(windowKey);
    const data = stored ? JSON.parse(stored) : { count: 0, windowStart: now };
    
    // Reset if window has passed
    if (now - data.windowStart > windowMs) {
      data.count = 0;
      data.windowStart = now;
    }
    
    // Check if limit exceeded
    if (data.count >= maxRequests) {
      return false;
    }
    
    // Increment counter
    data.count++;
    localStorage.setItem(windowKey, JSON.stringify(data));
    
    return true;
  } catch (error) {
    console.error('Rate limiting error:', error);
    return true; // Allow on error to prevent blocking
  }
};

/**
 * Simple encryption/decryption for localStorage data
 */
const encryptData = (data: string, key: string): string => {
  // Simple XOR encryption for client-side storage
  let encrypted = '';
  for (let i = 0; i < data.length; i++) {
    encrypted += String.fromCharCode(data.charCodeAt(i) ^ key.charCodeAt(i % key.length));
  }
  return btoa(encrypted);
};

const decryptData = (encryptedData: string, key: string): string => {
  try {
    const data = atob(encryptedData);
    let decrypted = '';
    for (let i = 0; i < data.length; i++) {
      decrypted += String.fromCharCode(data.charCodeAt(i) ^ key.charCodeAt(i % key.length));
    }
    return decrypted;
  } catch {
    return '';
  }
};

/**
 * Enhanced secure localStorage wrapper with encryption
 */
export const secureStorage = {
  setItem: (key: string, value: any, encrypt: boolean = false) => {
    try {
      const sanitizedKey = sanitizeInput(key);
      const serialized = JSON.stringify(value);
      
      // Validate data size (5MB limit)
      if (serialized.length > 5 * 1024 * 1024) {
        console.warn('Data too large for localStorage');
        return false;
      }
      
      // Add expiration timestamp for sensitive data
      const dataWithExpiry = {
        data: encrypt ? encryptData(serialized, 'tommy-roca-security-key') : serialized,
        encrypted: encrypt,
        expires: encrypt ? Date.now() + (24 * 60 * 60 * 1000) : null // 24 hours for encrypted data
      };
      
      localStorage.setItem(sanitizedKey, JSON.stringify(dataWithExpiry));
      return true;
    } catch (error) {
      console.error('Secure storage error:', error);
      return false;
    }
  },
  
  getItem: (key: string) => {
    try {
      const sanitizedKey = sanitizeInput(key);
      const stored = localStorage.getItem(sanitizedKey);
      if (!stored) return null;
      
      const parsed = JSON.parse(stored);
      
      // Check if data has expired
      if (parsed.expires && Date.now() > parsed.expires) {
        localStorage.removeItem(sanitizedKey);
        return null;
      }
      
      // Decrypt if necessary
      if (parsed.encrypted) {
        const decrypted = decryptData(parsed.data, 'tommy-roca-security-key');
        return decrypted ? JSON.parse(decrypted) : null;
      }
      
      return JSON.parse(parsed.data);
    } catch (error) {
      console.error('Secure storage retrieval error:', error);
      return null;
    }
  },
  
  removeItem: (key: string) => {
    try {
      const sanitizedKey = sanitizeInput(key);
      localStorage.removeItem(sanitizedKey);
      return true;
    } catch (error) {
      console.error('Secure storage removal error:', error);
      return false;
    }
  },
  
  // Clean up expired items
  cleanup: () => {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) {
          const stored = localStorage.getItem(key);
          if (stored) {
            try {
              const parsed = JSON.parse(stored);
              if (parsed.expires && Date.now() > parsed.expires) {
                keysToRemove.push(key);
              }
            } catch {
              // Invalid data, clean up
              keysToRemove.push(key);
            }
          }
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      return keysToRemove.length;
    } catch (error) {
      console.error('Storage cleanup error:', error);
      return 0;
    }
  }
};